import { useEffect, useState, useRef } from 'react';
import { Music } from 'lucide-react';
import { useAudio } from '@/context/AudioContext';

export function AudioPillAnimation() {
  const { animationSource, animationTargetRef, clearAnimation, currentTrack } = useAudio();
  const [animationStyle, setAnimationStyle] = useState<React.CSSProperties | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  // Track mount state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Start animation when source position is set
  useEffect(() => {
    if (!animationSource || !animationTargetRef.current) {
      return;
    }

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const targetRect = animationTargetRef.current.getBoundingClientRect();

    // Calculate start and end positions
    const startX = animationSource.left + animationSource.width / 2;
    const startY = animationSource.top + animationSource.height / 2;
    const endX = targetRect.left + targetRect.width / 2;
    const endY = targetRect.top + targetRect.height / 2;

    // Calculate translation
    const translateX = endX - startX;
    const translateY = endY - startY;

    // Set initial position (at source)
    setAnimationStyle({
      position: 'fixed',
      left: startX,
      top: startY,
      transform: 'translate(-50%, -50%) scale(1)',
      opacity: 1,
      zIndex: 9999,
      pointerEvents: 'none',
    });

    setIsAnimating(true);

    // Trigger animation after a frame
    requestAnimationFrame(() => {
      if (!isMountedRef.current) return;

      requestAnimationFrame(() => {
        if (!isMountedRef.current) return;

        setAnimationStyle({
          position: 'fixed',
          left: startX,
          top: startY,
          transform: `translate(calc(-50% + ${translateX}px), calc(-50% + ${translateY}px)) scale(0.3)`,
          opacity: 0,
          zIndex: 9999,
          pointerEvents: 'none',
          transition: 'all 600ms cubic-bezier(0.34, 1.56, 0.64, 1)',
        });
      });
    });

    // Clean up after animation
    timeoutRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;

      setIsAnimating(false);
      setAnimationStyle(null);
      clearAnimation();
    }, 700);

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [animationSource, animationTargetRef, clearAnimation]);

  if (!isAnimating || !animationStyle) return null;

  return (
    <div style={animationStyle}>
      <div className="bg-gradient-to-r from-[#8e4585] to-[#a855a8] text-white px-4 py-2 rounded-full flex items-center gap-2 shadow-xl">
        <Music size={16} />
        <span className="text-sm font-medium whitespace-nowrap max-w-[120px] truncate">
          {currentTrack?.title || 'Audio'}
        </span>
      </div>
    </div>
  );
}
